import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { jobDescriptionAPI } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';

// JobDescriptionTemplates — pagina SSM pentru a defini fișa postului (anexă SSM + SU).
// Două template-uri (SSM, SU), fiecare cu conținut bilingv RO + EN. Fișa postului se trimite
// automat la fiecare angajat al firmelor client în flow-ul lor de instruiri.
const JobDescriptionTemplates = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({ SSM: false, SU: false });

  // Conținut per domeniu — { contentRo, contentEn }
  const [templates, setTemplates] = useState({
    SSM: { contentRo: '', contentEn: '', version: 0, exists: false },
    SU: { contentRo: '', contentEn: '', version: 0, exists: false },
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await jobDescriptionAPI.getMyTemplates();
        if (!mounted) return;
        const next = { ...templates };
        for (const tpl of data.templates || []) {
          if (tpl.domain === 'SSM' || tpl.domain === 'SU') {
            next[tpl.domain] = {
              contentRo: tpl.contentRo || '',
              contentEn: tpl.contentEn || '',
              version: tpl.version || 1,
              exists: true,
            };
          }
        }
        setTemplates(next);
      } catch (err) {
        showToast(err.response?.data?.error || 'Eroare la încărcarea template-urilor', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (domain, key, value) => {
    setTemplates((prev) => ({ ...prev, [domain]: { ...prev[domain], [key]: value } }));
  };

  const handleSave = async (domain) => {
    const tpl = templates[domain];
    if (!tpl.contentRo?.trim()) {
      showToast(t('jobDescription.errorRoRequired', 'Conținutul RO este obligatoriu'), 'error');
      return;
    }
    if (!tpl.contentEn?.trim()) {
      showToast(t('jobDescription.errorEnRequired', 'Conținutul EN este obligatoriu'), 'error');
      return;
    }
    setSaving((prev) => ({ ...prev, [domain]: true }));
    try {
      const { data } = await jobDescriptionAPI.upsertTemplate(domain, {
        contentRo: tpl.contentRo,
        contentEn: tpl.contentEn,
      });
      setTemplates((prev) => ({
        ...prev,
        [domain]: { ...prev[domain], version: data.template?.version || 1, exists: true },
      }));
      showToast(t('jobDescription.saved', 'Template salvat cu succes'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Eroare la salvare', 'error');
    } finally {
      setSaving((prev) => ({ ...prev, [domain]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const renderDomainCard = (domain) => {
    const tpl = templates[domain];
    const isSaving = saving[domain];
    const titleColor =
      domain === 'SSM' ? 'text-blue-700 dark:text-blue-300' : 'text-red-700 dark:text-red-300';
    const ringColor =
      domain === 'SSM' ? 'ring-blue-200 dark:ring-blue-900' : 'ring-red-200 dark:ring-red-900';

    return (
      <div
        key={domain}
        className={`bg-white dark:bg-slate-900 rounded-xl shadow-sm ring-1 ${ringColor} p-6 mb-6`}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className={`text-xl font-bold ${titleColor}`}>
              {t('jobDescription.titleDomain', { domain })} —{' '}
              {domain === 'SSM' ? 'Securitatea și Sănătatea în Muncă' : 'Situații de Urgență'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {tpl.exists
                ? t('jobDescription.versionLabel', {
                    version: tpl.version,
                    defaultValue: `Versiune curentă: ${tpl.version}`,
                  })
                : t(
                    'jobDescription.notYetCreated',
                    'Încă nedefinit — angajații nu vor primi fișa postului până nu salvezi.'
                  )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Coloana RO */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              🇷🇴 {t('jobDescription.contentRo', 'Conținut în română')}
            </label>
            <RichTextEditor
              value={tpl.contentRo}
              onChange={(val) => updateField(domain, 'contentRo', val)}
              placeholder={t(
                'jobDescription.placeholderRo',
                'Scrie aici conținutul fișei postului în română...'
              )}
            />
          </div>

          {/* Coloana EN */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              🇬🇧 {t('jobDescription.contentEn', 'Content in English')}
            </label>
            <RichTextEditor
              value={tpl.contentEn}
              onChange={(val) => updateField(domain, 'contentEn', val)}
              placeholder={t(
                'jobDescription.placeholderEn',
                'Write the job description in English here...'
              )}
            />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={() => handleSave(domain)}
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition"
          >
            {isSaving
              ? t('jobDescription.saving', 'Se salvează...')
              : tpl.exists
                ? t('jobDescription.update', 'Actualizează template')
                : t('jobDescription.create', 'Salvează template')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          {t('jobDescription.pageTitle', 'Fișa postului')}
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          {t(
            'jobDescription.pageSubtitle',
            'Definește fișa postului SSM și SU pe care firma ta o trimite automat fiecărui angajat al firmelor client. Conținutul bilingv (RO/EN) apare pe documentul semnat de angajat și de administrator.'
          )}
        </p>
      </div>

      {renderDomainCard('SSM')}
      {renderDomainCard('SU')}
    </div>
  );
};

export default JobDescriptionTemplates;
